import { useState, useEffect, useMemo } from "react";
import useElectionsData from "../../hooks/useElectionsData";
import { findLastIndex } from "../../util";

type DateSelectProps = {
  yearSelectRef: React.RefObject<HTMLDivElement>;
  monthSelectRef: React.RefObject<HTMLDivElement>;
  isYearSelectOpen: boolean;
  isMonthSelectOpen: boolean;
  setIsYearSelectOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMonthSelectOpen: React.Dispatch<React.SetStateAction<boolean>>;
  name: string;
  setDate: React.Dispatch<React.SetStateAction<string>>;
};

const MONTHS: Record<string, string> = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

function DateSelect({
  yearSelectRef,
  monthSelectRef,
  name,
  setDate,
  isYearSelectOpen,
  isMonthSelectOpen,
  setIsYearSelectOpen,
  setIsMonthSelectOpen,
}: DateSelectProps) {
  const electionsData = useElectionsData();
  const distinctDates = useMemo(getDistinctDates, [electionsData]);

  const [yearOptions, setYearOptions] = useState<string[]>([]);
  const [year, setYear] = useState<string>("");
  const [month, setMonth] = useState<string>("");

  useEffect(() => {
    const dateIndex = name === "start-date" ? 0 : distinctDates.length - 1;
    setYear(getYear(distinctDates[dateIndex]));
    setMonth(getMonth(distinctDates[dateIndex]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setYearOptions(getYearOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distinctDates]);

  useEffect(() => {
    setDate(getSelectedDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  function getDistinctDates() {
    return electionsData.sheetData
      .map((sheetDataPoint) => sheetDataPoint.Date)
      .filter((date, index, self) => self.indexOf(date) === index);
  }

  function getYear(date: string) {
    return date.slice(6);
  }

  function getMonth(date: string) {
    return date.slice(0, 2);
  }

  function getYearOptions() {
    const options: string[] = [];

    distinctDates.forEach((date) => {
      const year = getYear(date);
      if (!options.includes(year)) options.push(year);
    });

    return options;
  }

  function getSelectedDate() {
    return distinctDates[
      name === "start-date"
        ? distinctDates.findIndex(
            (date) =>
              new Date(date).getTime() >= new Date(`${year}-${month}`).getTime()
          )
        : findLastIndex(
            distinctDates,
            (date) =>
              new Date(date).getTime() <=
              // get date with last day in selected month
              new Date(Number(year), Number(month), 0).getTime()
          )
    ];
  }

  return (
    <div className="us-elections-widget__date-select">
      <div ref={monthSelectRef} className="us-elections-widget__select">
        <p
          onClick={() => {
            setIsMonthSelectOpen(!isMonthSelectOpen);
          }}
          data-is-open={isMonthSelectOpen ? "true" : "false"}
        >
          {Object.keys(MONTHS).find((key) => MONTHS[key] === month)}
        </p>
        <ul style={{ display: isMonthSelectOpen ? "block" : "none" }}>
          {Object.entries(MONTHS).map(([label, value]) => (
            <li
              key={label}
              onClick={() => {
                setIsMonthSelectOpen(false);
                if (value === month) return;
                setMonth(value);
              }}
            >
              {label}
            </li>
          ))}
        </ul>
      </div>
      <div ref={yearSelectRef} className="us-elections-widget__select">
        <p
          onClick={() => {
            setIsYearSelectOpen(!isYearSelectOpen);
          }}
          data-is-open={isYearSelectOpen ? "true" : "false"}
        >
          {year}
        </p>
        <ul style={{ display: isYearSelectOpen ? "block" : "none" }}>
          {yearOptions.map((yearOption) => (
            <li
              key={yearOption}
              onClick={() => {
                setIsYearSelectOpen(false);
                if (yearOption === year) return;
                setYear(yearOption);
              }}
            >
              {yearOption}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default DateSelect;
